import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { dateFormatterHourMinute } from "../../../format";
import { typographies } from "../../../styles/figmaTypographies";
import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { TripOrPlanningOptionsDisplay } from "./DayTripDetails";
import { colors } from "../../../styles/figmaColors";
import { Badge } from "../Tag/Badge";
import { getBadgeColorByScore } from "../../getBadgeColorByScore";
import { Avatar } from "../Avatar/Avatar";

export interface DrivingEventBadgeProps {
  eventType: "smoothnessScore" | "supSpeedScore";
  score: number;
}

export interface TripOrPlanningDetailsProps {
  dataDisplay: TripOrPlanningOptionsDisplay;
  type: "TRIP" | "PLANNING";
  topPosition: number;
}

const formatHourAndMinutes = (value: number): string => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${hours}h${minutes < 10 ? "0" + minutes : minutes}`;
};

const tripDetailsOptionsDict = [
  "distance",
  "duration",
  "startDate",
  "endDate",
  "ecoDrivingScore",
];

const displayTripsDetailsByOptions = (
  option: (typeof tripDetailsOptionsDict)[number],
  detailsValue: string | number,
) => {
  switch (option) {
    case "distance":
    case "ecoDrivingScore":
      return Number(detailsValue).toFixed(1).toString();
    case "duration":
      return formatHourAndMinutes(detailsValue as number);
    case "startDate":
    case "endDate":
      return dateFormatterHourMinute(
        new Date(detailsValue as string),
      ).replaceAll(":", "h");
    default:
      return detailsValue.toString();
  }
};

const StyledTripContainer = styled.div`
  display: flex;
  background-color: ${colors["colors/button/secondary/hover"]};
  border-radius: 0.5rem;
  white-space: nowrap;
`;

export const TripOrPlanningDetailsVertical = (
  props: TripOrPlanningDetailsProps,
) => {
  const { t } = useTranslation();
  const { dataDisplay } = props;
  const { driver } = props.dataDisplay;

  const [firstName, lastName] = driver ? driver.split(" ") : ["", ""];

  return (
    <StyledAbsolute $top={props.topPosition}>
      <StyledFlexWrap>
        <StyledColumn>
          <StyledTripContainer>
            <Spacer x={0.75} />
            <StyledColumn>
              {driver && (
                <>
                  <Spacer y={1.5} />
                  <StyledRowCenter>
                    <TextCapitalized>
                      <Avatar
                        size="M"
                        color="mint"
                        firstName={firstName}
                        lastName={lastName}
                      />
                    </TextCapitalized>
                    <Spacer x={0.5} />
                    {firstName} {lastName}
                  </StyledRowCenter>
                </>
              )}
              <Spacer y={1.5} />
              <StyledFlexWrap>
                {tripDetailsOptionsDict.map((option, index) => {
                  const tripDetailsValue = dataDisplay[
                    option as keyof TripOrPlanningOptionsDisplay
                  ] as string | number;

                  const hasDataDisplayValue =
                    dataDisplay[
                      option as keyof TripOrPlanningOptionsDisplay
                    ] !== null &&
                    dataDisplay[
                      option as keyof TripOrPlanningOptionsDisplay
                    ] !== undefined;

                  return (
                    <div key={index + "trip"}>
                      {hasDataDisplayValue && (
                        <StyledFlexWrap>
                          <StyledColumn>
                            <StyledOptionTitle>
                              <TextCapitalized>
                                {t(`trips.details.${option}.title`)}
                              </TextCapitalized>
                            </StyledOptionTitle>
                            <Spacer y={0.5} />
                            <Badge
                              text={
                                <StyledBadge>
                                  <TextCapitalized>
                                    {displayTripsDetailsByOptions(
                                      option,
                                      tripDetailsValue,
                                    )}
                                  </TextCapitalized>
                                  {t(`trips.details.${option}.units`) !==
                                    "" && (
                                    <>
                                      <Spacer x={0.25} />
                                      {t(`trips.details.${option}.units`)}
                                    </>
                                  )}
                                </StyledBadge>
                              }
                              backgroundColor={getBadgeColorByScore(
                                option,
                                tripDetailsValue,
                              )}
                              textColor={colors["colors/text/black"]}
                              typo={typographies["Body/XS"]}
                            />
                          </StyledColumn>
                          {<Spacer x={1} />}
                        </StyledFlexWrap>
                      )}
                      <Spacer y={1} />
                    </div>
                  );
                })}
              </StyledFlexWrap>
              {props.dataDisplay.isOutIntervention &&
              props.type === "PLANNING" ? (
                <StyledFlexWrap>
                  <Spacer x={0.5} />
                  <StyledColumn>
                    <StyledOptionTitle>
                      <TextCapitalized>
                        {t(`trips.details.isOutIntervention.title`)}
                      </TextCapitalized>
                    </StyledOptionTitle>
                    <Spacer y={0.5} />
                    <StyledRowCenter>
                      <Badge
                        text={t(`trips.details.isOutIntervention.value`)}
                        backgroundColor={
                          colors[
                            "colors/system/informative/informative_veryLight"
                          ]
                        }
                        textColor={colors["colors/text/black"]}
                        typo={typographies["Body/XS"]}
                      />
                    </StyledRowCenter>
                    <Spacer x={1.5} />
                  </StyledColumn>
                  <Spacer x={1.5} />
                  <StyledColumn>
                    <StyledOptionTitle>
                      <TextCapitalized>
                        {t(`trips.details.activityType.title`)}
                      </TextCapitalized>
                    </StyledOptionTitle>
                    <Spacer y={1} />
                    <StyledRowCenter>
                      <TextCapitalized>
                        {props.dataDisplay.activityType ?? "-"}
                      </TextCapitalized>
                    </StyledRowCenter>
                    <Spacer y={1.5} />
                  </StyledColumn>
                  <Spacer x={1.5} />
                </StyledFlexWrap>
              ) : (
                <StyledFlexWrap>
                  {dataDisplay.clientName && (
                    <>
                      <Spacer x={0.5} />
                      <StyledFlexWrap>
                        <StyledColumn>
                          <StyledOptionTitle>
                            <TextCapitalized>
                              {t(`trips.details.clientName.title`)}
                            </TextCapitalized>
                          </StyledOptionTitle>
                          <Spacer y={0.5} />
                          <StyledRowCenter>
                            {dataDisplay.clientName}
                          </StyledRowCenter>
                        </StyledColumn>
                        <Spacer x={1.5} />
                      </StyledFlexWrap>
                    </>
                  )}
                  {dataDisplay.clientCity && (
                    <>
                      <StyledFlexWrap>
                        <StyledColumn>
                          <StyledOptionTitle>
                            <TextCapitalized>
                              {t(`trips.details.clientCity.title`)}
                            </TextCapitalized>
                          </StyledOptionTitle>
                          <Spacer y={0.5} />
                          <StyledRowCenter>
                            {dataDisplay.clientCity}
                            <Spacer x={1} />
                          </StyledRowCenter>
                        </StyledColumn>
                        <Spacer x={1.5} />
                      </StyledFlexWrap>
                    </>
                  )}

                  {dataDisplay.hasKmClient && (
                    <StyledFlexWrap>
                      <StyledColumn>
                        <StyledOptionTitle>
                          <TextCapitalized>
                            {t(`trips.details.hasKmClient.title`)}
                          </TextCapitalized>
                        </StyledOptionTitle>
                        <Spacer y={0.5} />
                        <StyledRowCenter>
                          <Badge
                            text={t(`trips.details.hasKmClient.value`)}
                            backgroundColor={
                              colors[
                                "colors/system/informative/informative_veryLight"
                              ]
                            }
                            textColor={colors["colors/text/black"]}
                            typo={typographies["Body/XS"]}
                          />
                          <Spacer x={1} />
                        </StyledRowCenter>
                        <Spacer y={1} />
                      </StyledColumn>
                    </StyledFlexWrap>
                  )}
                </StyledFlexWrap>
              )}
            </StyledColumn>
            <Spacer x={1} />
          </StyledTripContainer>
        </StyledColumn>
      </StyledFlexWrap>
    </StyledAbsolute>
  );
};

const StyledAbsolute = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}rem;
  pointer-events: none;
  z-index: 100;
  width: fit-content;
`;

export const StyledBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

export const StyledFlexWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
  max-width: 21rem;
`;

const StyledOptionTitle = styled.div`
  color: ${colors["colors/text/black"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
